import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { isTokenExpired } from '../auth/jwt.util';
import { LoginService } from '../auth/services/login.service';
import { HubSpotService } from './hub-spot.service';

@Injectable()
export class ErrorService {

  constructor(
    private loginService: LoginService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private hubSpotService: HubSpotService,
    private router: Router
  ) { }

  /**
   * Handles HTTP errors by displaying appropriate toast messages and logging the error.
   * 
   * @param {any} error - The error to handle.
   */
  public handleError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 401:
          console.trace('handleError 401', error);
          // Ya hay un cierre/renovación en curso: los 401 concurrentes de los
          // requests que salieron con el token viejo no deben competir.
          if (this.loginService.isSessionEnding) break;
          if (this.isExpiredToken401(error)) {
            this.handleExpiredSession();
            break;
          }
          // 401 => token expired or missing
          this.toastrService.info(this.translateService.instant('GLOBAL.APP_ERROR_401'));
          this.loginService.customLogout(this.router.url);
          break;

        case 503:
          // 503 ext_service_unavailable/auth_service_unavailable => transient
          // auth backend outage with a still-valid token. Must NOT log out
          // (unlike 401), the caller should retry shortly.
          console.trace('handleError 503', error);
          this.toastrService.warning(this.translateService.instant('GLOBAL.APP_ERROR_503'));
          break;

        case 403:
          this.showToastMessage(this.translateService.instant('GLOBAL.APP_ERROR_403'), {
            disableTimeOut: true,
            closeButton: true,
          }, () => {
            this.router.navigate(['companies']).then(() => {
              // Force refresh
              window.location.reload();
            });
          });
          break;

        case 404:
          this.showToastMessage(this.translateService.instant('GLOBAL.APP_ERROR_404'), {
            disableTimeOut: true,
            closeButton: true,
          }, () => {
            window.location.reload();
          });
          break;

        case 0:
          // Request abortado o sin red. Mientras la sesión se cierra/renueva
          // son los requests que corta la navegación (a /login o a Auth0): el
          // toast rojo de "contactá a soporte" no aplica.
          if (this.loginService.isSessionEnding) break;
          this.showToastMessage(this.translateService.instant('GLOBAL.APP_ERROR_DEFAULT'), {
            disableTimeOut: true
          }, () => {
            this.hubSpotService.open();
          });
          break;

        default:
          this.showToastMessage(this.translateService.instant('GLOBAL.APP_ERROR_DEFAULT'), {
            disableTimeOut: true
          }, () => {
            this.hubSpotService.open();
          });
          break;
      }
    } else {
      if (!environment.production) console.error(error);
    }
  }

  /**
   * 401 por token vencido o inválido (api2 responde `ext_token_invalid`), o el
   * token local ya venció. Se renueva la sesión sin bajar el SSO de Auth0.
   */
  private isExpiredToken401(error: HttpErrorResponse): boolean {
    // Sin sesión local no hay nada que renovar (p.ej. 401 del propio login).
    const token = this.loginService.getCurrentToken().value;
    if (!token) return false;

    const message = error.error?.message;
    if (message && typeof message === 'object' && 'ext_token_invalid' in message) return true;
    return isTokenExpired(token);
  }

  /**
   * Cartel amarillo (warning) en vez del rojo: no es un error del usuario ni de
   * la plataforma. Solo el primer 401 avisa; los concurrentes no suman toasts.
   */
  private handleExpiredSession(): void {
    const outcome = this.loginService.expireSession(this.router.url);

    if (outcome === 'soft') {
      this.toastrService.warning(this.translateService.instant(
        this.loginService.usesAuth0 ? 'GLOBAL.APP_SESSION_RENEWING' : 'GLOBAL.APP_ERROR_401'
      ));
    } else if (outcome === 'hard') {
      // Anti-bucle: logout completo, mismo aviso que antes.
      this.toastrService.info(this.translateService.instant('GLOBAL.APP_ERROR_401'));
    }
  }

  /**
   * Displays a toast message with the given options and executes a callback function when the toast is hidden.
   * 
   * @param {string} message - The message to display in the toast.
   * @param {Partial<IndividualConfig>} [options=null] - The options to customize the toast.
   * @param {Function} [onHidden] - The callback function to execute when the toast is hidden.
   * @private
   */
  private showToastMessage(message: string, options: Partial<IndividualConfig> = null, onHidden?: Function): void {
    const toast = this.toastrService.error(message, undefined, options);

    if (onHidden) toast.onHidden.pipe(take(1)).subscribe(() => {
      onHidden();
    });
  }
}
